import {canUseCapability, planOf, type Plan} from "../home/gating";

/**
 * Who gets the priority-support framing (kitecorp/transferify-api-service#101).
 *
 * The rule is a CATALOGUE lookup, never a tier comparison: the answer is the `prioritySupport`
 * capability that `/plan` bakes per tier. Writing `plan === "premium"` here would put a second,
 * hand-maintained copy of a pricing decision in the UI — so moving the capability down a tier in the
 * api would silently keep promising the SLA to the wrong people and withholding it from the right
 * ones. Reading the capability means the pricing page, the api's own gate and this dialog can only
 * ever agree.
 *
 * React-free by mandate (epic #218): the framing survives any restyle of the dialog above it.
 */

/**
 * How the support form presents itself. `"priority"` earns the SLA promise; `"standard"` is the exact
 * same form, best-effort, with no promise attached — lower tiers are not offered a worse channel, only
 * an honest one.
 */
export type SupportFraming = "priority" | "standard";

/**
 * The subscription facts the framing depends on — structurally the same subset `planOf` reads from
 * `/account`, taken from it directly so a change to that contract is a compile error here rather than
 * a silently diverging duplicate.
 */
export type SupportEntitlementUser = Parameters<typeof planOf>[0];

/** The framing a plan earns. */
export function supportFramingOf(plan: Plan): SupportFraming {
	return canUseCapability(plan, "prioritySupport") ? "priority" : "standard";
}

/** Whether a plan carries the priority-support entitlement. */
export function hasPrioritySupport(plan: Plan): boolean {
	return supportFramingOf(plan) === "priority";
}

/**
 * The framing a signed-in user earns, resolved end to end so the React layer holds no part of the
 * rule. `planOf` already collapses a lapsed or unrecognised subscription to `free`, which is what
 * withdraws the promise together with the plan that funds it.
 */
export function supportFramingForUser(user: SupportEntitlementUser): SupportFraming {
	return supportFramingOf(planOf(user));
}

/**
 * The framing a VISITOR earns — the same rule, plus the one fact a user object cannot answer: whether
 * anyone is signed in at all.
 *
 * Anonymous is never priority. A logged-out visitor has no subscription to read, and the persisted
 * user model can still be carrying the last session's plan, so asking `supportFramingForUser` alone
 * would promise an SLA to someone the api will not even authenticate.
 *
 * It lives here rather than at the two call sites — the Resources row's LABEL (`nav/normal/NavBar`)
 * and the dialog's FRAMING (`useSupportRequest`) — because those two must agree: a row that says
 * "Priority support" opening a form with no SLA promise, or the reverse, is worse than either. Since
 * #520 they are the same entry, so they read the same function.
 *
 * `isLoggedIn` is expected to come from `useIsLoggedIn`, which is false during the pre-render and the
 * first client render; that is what keeps an SLA promise from flashing in or out on hydration.
 */
export function supportFramingForVisitor(
	isLoggedIn: boolean,
	user: SupportEntitlementUser,
): SupportFraming {
	return isLoggedIn ? supportFramingForUser(user) : "standard";
}
